import {
  AlertTriangle, Bell, CheckCircle2, ChevronDown, ClipboardCheck, FileText, MapPin,
  MessageCircle, MoreHorizontal, Search,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import projectImage from "@/assets/riyadh-villa-construction.jpg";
import siteProgressOne from "@/assets/tiling-zone-b-1.png";
import siteProgressTwo from "@/assets/tiling-zone-b-2.png";
import siteProgressWide from "@/assets/gulf-villa-construction.jpg";
import phasesTasks from "@/assets/phases-tasks-ai.png";

const ManagementDashboardMockup = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-[650px] min-w-0 overflow-hidden rounded-2xl bg-[#f4f6f8] pb-2 text-left text-slate-900 shadow-[0_20px_50px_rgba(0,0,0,0.28)]">
      <div className="min-w-0 flex-1">
        <div className="relative flex h-11 items-center justify-between border-b border-slate-200 bg-white px-3">
          <div className="flex items-center gap-1.5"><p className="text-[10px] font-bold">{t("heroMockup.dashboard")}</p><ChevronDown className="h-3 w-3 text-slate-400" /></div>
          <div className="absolute left-1/2 top-1/2 hidden h-8 w-[46%] -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-[8px] text-slate-400 sm:flex">
            <Search className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{t("heroMockup.searchProject")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Bell className="h-3.5 w-3.5 text-slate-400" />
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[7px] font-bold text-white">OM</div>
          </div>
        </div>

        <div className="mx-2 mt-2 grid h-[137px] grid-cols-[120px_minmax(0,1fr)] overflow-hidden rounded-xl border border-slate-200 bg-white sm:grid-cols-[170px_minmax(0,1fr)]">
          <div className="relative overflow-hidden">
            <img src={projectImage} alt="" className="h-full w-full object-cover" />
            <span className="absolute left-2 top-2 rounded-full bg-white/95 px-2 py-1 text-[7px] font-bold text-emerald-600 shadow-sm">● {t("heroMockup.onTrack")}</span>
          </div>
          <div className="min-w-0 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><p className="truncate text-[15px] font-bold">Marina Villa</p><div className="mt-1 flex items-center gap-1 text-[7px] text-slate-400"><MapPin className="h-2.5 w-2.5" />Dubai Marina, UAE · Structural framing</div></div>
              <span className="text-[18px] font-black tracking-tight">50%</span>
            </div>
            <div className="mt-2 grid grid-cols-5 gap-1.5">
              {[
                [t("heroMockup.foundation"), "w-full", "bg-emerald-500", "bg-emerald-500"],
                [t("heroMockup.structure"), "w-1/2", "bg-amber-500", "bg-amber-500"],
                [t("heroMockup.mep"), "w-0", "bg-slate-300", "bg-slate-300"],
                [t("heroMockup.finishes"), "w-0", "bg-slate-300", "bg-slate-300"],
                [t("heroMockup.finalInspection"), "w-0", "bg-slate-300", "bg-slate-300"],
              ].map(([label, width, barColor, dotColor]) => (
                <div key={label} className="min-w-0">
                  <div className="flex items-center justify-center gap-1"><span className="truncate text-[6px] font-semibold text-slate-500">{label}</span><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotColor}`} /></div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200"><div className={`h-full rounded-full ${width} ${barColor}`} /></div>
                </div>
              ))}
            </div>
            <div className="mt-2.5 flex items-center justify-between">
              <div className="flex items-center gap-1 text-[7px] font-semibold text-slate-500"><CheckCircle2 className="h-3 w-3 text-emerald-500" /><strong className="text-slate-800">5/10</strong> {t("heroMockup.completedTasks")}</div>
              <div className="flex -space-x-1.5">{["bg-rose-400", "bg-amber-500", "bg-sky-500", "bg-emerald-500"].map((color, index) => <div key={color} className={`flex h-5 w-5 items-center justify-center rounded-full border-2 border-white text-[6px] font-bold text-white ${color}`}>{["O", "A", "R", "+3"][index]}</div>)}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 p-2 sm:h-[300px] sm:grid-cols-[1.42fr_0.88fr]">
          <div className="min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex h-9 items-center justify-between border-b border-slate-100 px-3">
              <div className="flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5 text-blue-600" /><p className="text-[10px] font-bold">{t("features.projectFeed.label")}</p></div>
              <span className="flex items-center gap-1 text-[7px] text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{t("heroMockup.live")}</span>
            </div>
            <div className="relative px-3 py-2">
              <div className="absolute bottom-3 left-[53px] top-3 w-px bg-slate-200" />
              <div className="relative grid grid-cols-[38px_12px_minmax(0,1fr)] gap-2 pb-3">
                <p className="pt-1 text-[7px] font-semibold text-slate-400">{t("heroMockup.today")}</p>
                <div className="relative z-10 mt-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500 ring-1 ring-emerald-200" />
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                  <div className="flex items-center justify-between gap-2"><p className="truncate text-[9px] font-bold">{t("heroMockup.inspectionPassed")}</p><span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[6px] font-bold text-emerald-700">UPDATE</span></div>
                  <p className="mt-1 line-clamp-2 text-[7px] leading-snug text-slate-500">{t("heroMockup.evidenceSaved")}</p>
                  <div className="mt-2 grid grid-cols-[64px_minmax(0,1fr)] gap-1.5">
                    <div className="relative h-10 overflow-hidden rounded-md">
                      <img src={siteProgressOne} alt="Verified construction progress" className="h-full w-full object-cover" />
                      <span className="absolute bottom-1 left-1 rounded bg-black/55 px-1 text-[5px] font-semibold text-white">ZONE B</span>
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white p-1.5"><FileText className="h-3.5 w-3.5 shrink-0 text-blue-500" /><span className="truncate text-[7px] font-semibold">Safety_Report_W12.pdf</span></div>
                  </div>
                </div>
              </div>
              <div className="relative grid grid-cols-[38px_12px_minmax(0,1fr)] gap-2 pb-3">
                <p className="pt-1 text-[7px] font-semibold text-slate-400">09:42</p>
                <div className="relative z-10 mt-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-500 ring-1 ring-blue-200" />
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                  <div className="flex items-center justify-between"><p className="text-[9px] font-bold">{t("heroMockup.foreman")}</p><MoreHorizontal className="h-3 w-3 text-slate-400" /></div>
                  <p className="mt-1 line-clamp-2 text-[7px] leading-snug text-slate-500">{t("heroMockup.update")}</p>
                  <div className="mt-2 grid grid-cols-3 gap-1">
                    {[
                      [siteProgressOne, "Level 2"],
                      [siteProgressTwo, "Finishing"],
                      [siteProgressWide, "Overview"],
                    ].map(([src, label]) => (
                      <div key={src} className="relative h-10 overflow-hidden rounded bg-slate-200">
                        <img src={src} alt={`${label} construction update`} className="h-full w-full object-cover" />
                        <span className="absolute inset-x-0 bottom-0 truncate bg-black/45 px-1 py-0.5 text-[5px] font-medium text-white">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button type="button" className="ml-[60px] flex h-6 w-fit items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-[7px] font-semibold text-blue-600 transition-colors hover:bg-blue-50">{t("heroMockup.viewMore")}</button>
            </div>
          </div>

          <div className="min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex h-9 items-center justify-between border-b border-slate-100 px-3">
              <div className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-blue-500" /><p className="text-[10px] font-bold">{t("heroMockup.risks")}</p></div>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-50 text-[8px] font-bold text-red-500">3</span>
            </div>
            <div className="space-y-2 p-2">
              {[[t("heroMockup.riskItem"), t("heroMockup.riskMeta"), "CRITICAL", "border-red-200 bg-red-50 text-red-600"], [t("heroMockup.deadlineItem"), t("heroMockup.dueTomorrow"), "HIGH", "border-amber-200 bg-amber-50 text-amber-600"], [t("heroMockup.nextTask"), t("heroMockup.nextTaskMeta"), "MEDIUM", "border-blue-200 bg-blue-50 text-blue-600"]].map(([title, description, severity, color]) => (
                <div key={severity} className="rounded-lg border border-slate-200 p-2.5">
                  <div className="flex items-start justify-between gap-2"><p className="text-[9px] font-bold leading-snug">{title}</p><span className={`shrink-0 rounded border px-1.5 py-0.5 text-[6px] font-bold ${color}`}>{severity}</span></div>
                  <p className="mt-2 line-clamp-2 text-[7px] leading-snug text-slate-500">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="flex h-9 items-center border-b border-slate-100 px-3">
            <ClipboardCheck className="h-3.5 w-3.5 text-blue-600" />
            <p className="ml-1.5 text-[10px] font-bold">{t("heroMockup.phasesTasks")}</p>
          </div>
          <div className="aspect-[1111/526]">
            <img src={phasesTasks} alt="Construction phases, tasks, assignees, evidence, and completion status" className="block h-full w-full object-cover" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagementDashboardMockup;
